// Mirrors apps/api/src/lib/policySigning.ts. Policy documents are the
// handbook, the leave policy and any type HR adds (POLICY_DOCUMENT lookup);
// HR assigns which ones each employee signs.
export type PolicyKind = string;

/** One piece of a document's text, as Claude read it on upload. */
export interface PolicyBlock {
  kind: "heading" | "subheading" | "paragraph" | "bullet" | "numbered" | "table";
  /** The printed number or letter ("1.", "2.3", "(a)"), or null. */
  number: string | null;
  /** For a paragraph, a tab separates labels side by side ("NAME:	SIGNATURE:"). */
  text: string;
  /** A table's cells, row by row, header first; absent or null for other kinds. */
  rows?: string[][] | null;
}

export interface PolicyDoc {
  kind: PolicyKind; typeName: string; title: string; version: string; readUrl: string;
  /** Every employee signs it (and again each 1 January); otherwise HR asked this employee. */
  allEmployees: boolean;
  /** Signed before, same version: this is the yearly signature. */
  yearly: boolean;
  /** The extracted text; null shows the PDF instead. */
  content: PolicyBlock[] | null;
}

export interface SignedPolicy {
  kind: PolicyKind;
  typeName: string;
  title: string;
  version: string | null;
  signedName: string;
  signedAt: string;
  /** Saves the stamped signed copy. */
  downloadUrl: string | null;
}

export interface PolicySigningStatus {
  required: boolean;
  /** The employee's full name on record: what their signature will read. */
  signerName: string;
  outstanding: PolicyDoc[];
  signed: SignedPolicy[];
}

/** HR's per-employee view: GET /employees/:id/policy-assignments. */
export interface PolicyAssignmentRow {
  kind: PolicyKind;
  typeName: string;
  current: { title: string; version: string } | null;
  /** The current version is signed by every employee (and yearly): not HR's choice. */
  allEmployees: boolean;
  assigned: boolean;
  /** Waiting for this employee's signature now. */
  due: boolean;
  /** The latest signature. */
  signature: SignedPolicy | null;
  /** Every signature, newest first. */
  signatures: SignedPolicy[];
}

/** Fired on window when what an employee must sign may have changed (e.g. HR published a version). */
export const POLICY_STATUS_EVENT = "hrms:policy-status-changed";

export const EMPTY_POLICY_STATUS: PolicySigningStatus = { required: false, signerName: "", outstanding: [], signed: [] };
